/**
 * EngineDetailStoretext Component
 *
 * Engine-specific detail view for Storetext Engine.
 * Delegates to the rich StoretextOverallCard and StoretextSectionCard components.
 */

import { useState } from "react";
import type { StoretextEngineSpecificAnalysis, StoretextSemanticPreprocessingOutput, UnifiedDetailedAnalysis } from "@a7y/types";
import { StoretextOverallCard } from "@/components/audit/storetext/StoretextOverallCard";
import { StoretextSectionCard } from "@/components/audit/storetext/StoretextSectionCard";
import { SemanticClustersCard } from "@/components/audit/storetext/SemanticClustersCard";
import { SemanticConsistencyCard } from "@/components/audit/storetext/SemanticConsistencyCard";
import { FactualGroundingSection } from "@/components/audit/storetext/FactualGroundingSection";
import { CollapsibleSection } from "./CollapsibleSection";
import { buildStoretextOverallForDisplay } from "./storetextOverallDisplay";

export interface EngineDetailStoretextProps {
  /** Storetext-specific analysis data */
  analysis: StoretextEngineSpecificAnalysis;
  /** Detailed analysis with score_breakdown */
  detail: UnifiedDetailedAnalysis;
  /** Semantic preprocessing output (from intermediate data) */
  semanticPreprocessing?: StoretextSemanticPreprocessingOutput;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function EngineDetailStoretext({ analysis, detail, semanticPreprocessing, printMode, className = "" }: EngineDetailStoretextProps) {
  const {
    overall_analysis,
    title_analysis,
    subtitle_analysis,
    short_description_analysis,
    description_analysis,
  } = analysis;

  const [clustersOpen, setClustersOpen] = useState(true);
  const [overallOpen, setOverallOpen] = useState(true);
  const [titleOpen, setTitleOpen] = useState(true);
  const [subtitleOpen, setSubtitleOpen] = useState(true);
  const [shortDescOpen, setShortDescOpen] = useState(true);
  const [descriptionOpen, setDescriptionOpen] = useState(true);
  const [consistencyOpen, setConsistencyOpen] = useState(true);
  const [factualOpen, setFactualOpen] = useState(true);

  const overallForDisplay = overall_analysis
    ? buildStoretextOverallForDisplay(overall_analysis, detail)
    : null;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Semantic Clusters Overview */}
      {semanticPreprocessing && (
        <CollapsibleSection
          title="Semantic Clusters"
          isExpanded={clustersOpen}
          onToggle={() => setClustersOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <SemanticClustersCard clusters={semanticPreprocessing.semantic_clusters} bare />
        </CollapsibleSection>
      )}

      {/* Overall Analysis */}
      {overallForDisplay && (
        <CollapsibleSection
          title="Overall Analysis"
          isExpanded={overallOpen}
          onToggle={() => setOverallOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <StoretextOverallCard
            overallAnalysis={overallForDisplay}
            store={short_description_analysis ? "playstore" : "appstore"}
            bare
          />
        </CollapsibleSection>
      )}

      {/* Title Analysis */}
      {title_analysis && (
        <CollapsibleSection
          title="Title"
          isExpanded={titleOpen}
          onToggle={() => setTitleOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <StoretextSectionCard
            title="Title"
            sectionEnvelope={title_analysis}
            maxLength={title_analysis.text_metrics.max_length}
            bare
          />
        </CollapsibleSection>
      )}

      {/* Subtitle Analysis (App Store) */}
      {subtitle_analysis && (
        <CollapsibleSection
          title="Subtitle"
          isExpanded={subtitleOpen}
          onToggle={() => setSubtitleOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <StoretextSectionCard
            title="Subtitle"
            sectionEnvelope={subtitle_analysis}
            maxLength={subtitle_analysis.text_metrics.max_length}
            bare
          />
        </CollapsibleSection>
      )}

      {/* Short Description Analysis (Play Store) */}
      {short_description_analysis && (
        <CollapsibleSection
          title="Short Description"
          isExpanded={shortDescOpen}
          onToggle={() => setShortDescOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <StoretextSectionCard
            title="Short Description"
            sectionEnvelope={short_description_analysis}
            maxLength={short_description_analysis.text_metrics.max_length}
            bare
          />
        </CollapsibleSection>
      )}

      {/* Description Analysis */}
      {description_analysis && (
        <CollapsibleSection
          title="Description"
          isExpanded={descriptionOpen}
          onToggle={() => setDescriptionOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <StoretextSectionCard
            title="Description"
            sectionEnvelope={description_analysis}
            maxLength={description_analysis.text_metrics.max_length}
            bare
          />
        </CollapsibleSection>
      )}

      {/* Cross-Field Consistency */}
      {semanticPreprocessing && (
        <CollapsibleSection
          title="Cross-Field Consistency"
          isExpanded={consistencyOpen}
          onToggle={() => setConsistencyOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <SemanticConsistencyCard consistency={semanticPreprocessing.consistency_analysis} bare />
        </CollapsibleSection>
      )}

      {/* Factual Grounding */}
      {analysis.factual_grounding && (
        <CollapsibleSection
          title="Factual Grounding"
          isExpanded={factualOpen}
          onToggle={() => setFactualOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <FactualGroundingSection report={analysis.factual_grounding} bare />
        </CollapsibleSection>
      )}
    </div>
  );
}
